import type * as React from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';

export interface AdjustmentNoteCoreFieldsProps {
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly errors: FieldErrors<AdjustmentNoteSubmitValues>;
  readonly disabled: boolean;
  readonly currencyLocked: boolean;
  readonly noteType: 'Credit' | 'Debit';
  readonly side: 'buy' | 'sell';
}

export function NumberInput(args: {
  readonly id: 'total_amount' | 'vat_amount';
  readonly label: string;
  readonly props: AdjustmentNoteCoreFieldsProps;
}) {
  const { id, label, props } = args;
  return (
    <FieldShell id={id} label={label} error={errorMessage(props.errors, id)}>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        disabled={props.disabled}
        {...props.register(id, { valueAsNumber: true })}
      />
    </FieldShell>
  );
}

export function FieldShell({
  id,
  label,
  error,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly error: string | undefined;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error !== undefined && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function errorMessage(
  errors: FieldErrors<AdjustmentNoteSubmitValues>,
  key: keyof AdjustmentNoteSubmitValues,
): string | undefined {
  const node = (errors as Record<string, unknown>)[key];
  if (node !== null && typeof node === 'object' && 'message' in node) {
    const msg = (node as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}
